import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import type { ClientPreferenceData, CreateClientFormData, MeasurementSystem } from '@/types/clientsApiTypes';
import { nullableString } from '@/utils/validations';
import { useUpdateClientPreferencesMutation } from '../../../hooks/useUpdateClientPreferencesMutation';
import { EditRelatedComfortDetailsForm } from '../../EditSectionModals/components/EditRelatedComfortDetailsForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    preference_data: yup.object().shape({
      personal: yup.object().shape({
        personal_metric_system: yup.string<MeasurementSystem>().oneOf(['us', 'metric']).required().default('us'),
        information: nullableString().defined().max(300, 'Information must not exceed 300 characters'),
        comfort_related: yup.object().shape({
          us: yup.object().shape({
            height: nullableString().defined(),
            weight: nullableString().defined(),
          }),
          metric: yup.object().shape({
            height: nullableString().defined(),
            weight: nullableString().defined(),
          }),
        }),
      }),
    }),
  }),
});

type ComfortDetailsFormValues = yup.InferType<typeof schema>;

type ComfortDetailsPayload = {
  preference_data: Partial<ClientPreferenceData>;
  clientId: number;
};

function useComfortDetailsOnSave() {
  const mutation = useUpdateClientPreferencesMutation();
  const queryClient = useQueryClient();

  return async (payload: ComfortDetailsPayload) => {
    await mutation.mutateAsync(
      {
        preference_data: payload.preference_data,
        clientId: payload.clientId,
      },
      {
        onSuccess: () => {
          toast.success('Comfort details updated successfully');
          queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
        },
      }
    );
  };
}

function mapClientToValues(client: CreateClientFormData): ComfortDetailsFormValues {
  const personal = client?.primary_passenger?.preference_data?.personal;
  return {
    primary_passenger: {
      preference_data: {
        personal: {
          personal_metric_system: (personal?.personal_metric_system as MeasurementSystem) ?? 'us',
          information: personal?.information ?? null,
          comfort_related: {
            us: {
              height: personal?.comfort_related?.us?.height ?? null,
              weight: personal?.comfort_related?.us?.weight ?? null,
            },
            metric: {
              height: personal?.comfort_related?.metric?.height ?? null,
              weight: personal?.comfort_related?.metric?.weight ?? null,
            },
          },
        },
      },
    },
  };
}

function mapValuesToPayload(
  values: ComfortDetailsFormValues,
  client: CreateClientFormData | null | undefined
): ComfortDetailsPayload {
  const clientId = client?.primary_passenger?.client?.client_id ?? 0;
  const personal = values.primary_passenger.preference_data.personal;
  return {
    preference_data: {
      personal: {
        comfort_related: {
          us: {
            height: personal.comfort_related?.us?.height ?? undefined,
            weight: personal.comfort_related?.us?.weight ?? undefined,
          },
          metric: {
            height: personal.comfort_related?.metric?.height ?? undefined,
            weight: personal.comfort_related?.metric?.weight ?? undefined,
          },
        },
        information: personal.information ?? undefined,
      },
    },
    clientId,
  };
}

export const relatedComfortDetailsSection = defineSection<
  (typeof ModalKeys)['related_comfort_details'],
  CreateClientFormData,
  typeof schema,
  ComfortDetailsPayload
>({
  key: ModalKeys.related_comfort_details,
  title: 'Comfort-related details',
  subtitle: 'This information helps us plan a more comfortable travel experience.',
  schema,
  mapClientToValues,
  mapValuesToPayload,
  useOnSave: useComfortDetailsOnSave,
  Fields: EditRelatedComfortDetailsForm,
});
